import React, { useMemo } from 'react'
import {
  Field,
  SelectOption,
  PlainTextInput,
  InputSelect,
} from '@Knowledge-OTP/znk-ui-components'
import useTranslation from '../../../../i18n/useTranslation'
import Services from '../../../../Model/Services/List'
import { escapeRegExp } from 'lodash'

const LessonDetails = (props) => {
  const { form, setForm } = props
  const { translate } = useTranslation()
  const { data: servicesInfo } = Services({ limit: 250 })
  // ✅ Memoizar servicios para evitar recálculos innecesarios
  const services = useMemo(() => {
    return servicesInfo?.docs || []
    // eslint-disable-next-line
  }, [servicesInfo?.docs])
  
  // ✅ Memoizar el servicio seleccionado con validación
  const selectedService = useMemo(() => {
    if (!form?.service?.id || !services.length) return null
    return services.find(service => service.id === form.service.id)
    // eslint-disable-next-line
  }, [form?.service?.id, services])
  
  // ✅ Memoizar topics con deduplicación por ID
  const topics = useMemo(() => {
    if (!selectedService?.topics) return []
    
    // ✅ Eliminar duplicados por ID y validar que tengan nombre
    const uniqueTopics = selectedService.topics.reduce((acc, topic) => {
      if (topic.id && topic.name && !acc.find(t => t.id === topic.id)) {
        acc.push(topic)
      }
      return acc
    }, [])
    
    return uniqueTopics
    // eslint-disable-next-line
  }, [selectedService?.topics])
  
  // ✅ Memoizar opciones de servicios
  const serviceOptions = useMemo(() => {
    return services.map((serv) => ({
      value: serv.id,
      label: serv.name
    }))
  }, [services])
  
  // ✅ Memoizar opciones de topics
  const topicOptions = useMemo(() => {
    return topics.map((top) => ({
      value: top.id,
      label: top.name
    }))
  }, [topics])
  
  // ✅ Función para manejar cambio de servicio
  const handleServiceChange = (serviceId) => {
    const selectedService = services.find(serv => serv.id === serviceId)
    
    if (selectedService) {
      setForm({
        ...form,
        service: { id: serviceId, name: selectedService.name },
        topic: null, // ✅ Limpiar topic cuando cambia el servicio
        title: '' // ✅ Limpiar título también
      })
    }
  }
  
  // ✅ Función para manejar cambio de topic
  const handleTopicChange = (topicId) => {
    const selectedTopic = topics.find(top => top.id === topicId)
    
    if (selectedTopic) {
      setForm({
        ...form,
        topic: { id: topicId, name: selectedTopic.name },
        title: `${form?.service?.name || ''} ${selectedTopic.name}`.trim()
      })
    }
  }

  return (
    <>
      <div className='m-0 p-0'>
        <h3><b>{translate('modal.newlesson.lessondetails')}</b></h3>
        <div className='row pl-3'>
          <label className='col-12 text-muted mb-2 mt-3 pl-0'>
            {translate('modal.newlesson.lessontype')}
          </label>
          <Field
            className='col-12 row inline'
            type={SelectOption}
            fieldName='typeLesson'
            options={[
              { id: 'individual', text: translate('modal.newlesson.individual') },
              { id: 'group', text: translate('modal.newlesson.group') }
            ]}
            value={form.typeLesson}
            StyleOptionClassName={{
              width: '49%',
              whiteSpace: 'nowrap'
            }}
            StyleSelect={{
              justifyContent: 'space-between'
            }}
          />
          <label className='col-12 text-muted mt-2 mb-2 mt-3 pl-0'>
            {translate('modal.newlesson.service')}
          </label>
          <Field
            InputClassName='col-12 p-0'
            type={InputSelect}
            fieldName='service'
            placeholder={translate('modal.newlesson.selectservice')}
            value={form?.service?.id}
            options={serviceOptions}
            filterOption={(obj, string) => {
              if (new RegExp(escapeRegExp(string), 'i').test(obj.label)) return true
              return false
            }}
            onChange={handleServiceChange}
          />
          <label className='col-12 text-muted mt-2 mb-2 mt-3 pl-0'>
            {translate('modal.newlesson.topic')}
          </label>
          <Field
            InputClassName='col-12 p-0'
            type={InputSelect}
            filterOption={(obj, string) => {
              if (new RegExp(escapeRegExp(string), 'i').test(obj.label)) return true
              return false
            }}
            fieldName='topic'
            placeholder={translate('modal.newlesson.selecttopic')}
            value={form?.topic?.id}
            options={topicOptions}
            onChange={handleTopicChange}
            disabled={!selectedService} // ✅ Deshabilitar si no hay servicio seleccionado
          />
          <label className='col-12 text-muted mt-2 mb-2 mt-3 pl-0'>
            {translate('modal.newlesson.titlelabel')}
          </label>
          <Field
            className='col-12 w-100'
            type={PlainTextInput}
            fieldName='title'
            placeholder={translate('modal.newlesson.titlelabel')}
            value={form.title}
          />
          <label className='col-12 text-muted mt-2 mb-2 mt-3 pl-0'>
            {translate('modal.newplan.location')}
          </label>
          <Field
            className='col-12'
            type={SelectOption}
            fieldName='locationLesson'
            showNotSelected
            changeFunction={() => {
              setForm({ ...form, locationLesson: null })
            }}
            changeLabel={translate('modal.newplan.changelocation')}
            options={[
              { id: 'virtualClassroom', text: 'Virtual Classroom' },
              { id: 'zinkerzPlatform', text: translate('modal.newplan.plandetails.zinkerzp') },
              { id: 'hangouts', text: 'Meets' },
              { id: 'zoom', text: 'Zoom' },
              { id: 'inPerson', text: translate('modal.newplan.plandetails.inperson') }
            ]}
            value={form.locationLesson}
          />
          {['inPerson', 'hangouts', 'zoom'].indexOf(form.locationLesson) !== -1 && (
            <>
              <label className='col-12 text-muted mt-2 pl-0'>
                {form.locationLesson === 'inPerson' ? translate('modal.newplan.address') : translate('modal.newplan.locationlink')}
              </label>
              <Field
                className='col-12'
                type={PlainTextInput}
                fieldName='locationLessonLink'
                placeholder={form.locationLesson === 'inPerson' ? translate('modal.newplan.address') : 'http://zoom.com/ASDF'}
                value={form.locationLessonLink}
              />
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default LessonDetails
